import React, { useState, useRef } from "react";
import {
  Avatar,
  IconButton,
  Typography,
  Divider,
  Button,
  Input,
  InputAdornment,
  Backdrop,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Create, Check, Close } from "@material-ui/icons";
import { storage, firestore } from "../../../../firebase";
import { doc, getDoc, updateDoc, deleteField } from "@firebase/firestore";
import { ref, deleteObject } from "@firebase/storage";

import { useAuth } from "../../../../contexts/AuthContext";
import UploadImage from "./UploadImage";

import Logo from "../../../../getKampus.png";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: 10,
    color: "#fff",
  },
  profilePicDiv: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  profilePic: {
    width: theme.spacing(25),
    height: theme.spacing(25),
    marginBottom: theme.spacing(2),
    backgroundColor: "white",
    // padding: theme.spacing(4),
    border: "2px solid #f0f0f0",
    boxShadow: "3px 5px 7px rgba(0,0,0,0.5)",
    "&:hover": {
      cursor: "pointer",
    },
  },
  nameDiv: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  nameEdit: {
    marginTop: theme.spacing(2),
    fontSize: 18,
  },
  emailDiv: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
}));

export default function EditProfile({ currentUser, setEdit }) {
  const classes = useStyles();
  const { updateProfilePicture, updateUserName, updateUserEmail } = useAuth();
  const [error, setError] = useState("");
  const [editName, setEditName] = useState(false);
  const [editEmail, setEditEmail] = useState(false);
  const [image, setImage] = useState(null);
  const [url, setUrl] = useState("");
  const [pathname, setPathname] = useState("");
  const nameRef = useRef();
  const emailRef = useRef();
  const [loading, setLoading] = useState(false);

  const handleNameChange = async (event) => {
    event.preventDefault();
    try {
      await updateUserName(nameRef.current.value);
      setEditName(false);
    } catch (err) {
      setError("Name Change Error");
      setEditName(false);
    }
  };

  const handleEmailChange = async (event) => {
    event.preventDefault();
    try {
      await updateUserEmail(emailRef.current.value);
      setEditEmail(false);
    } catch (err) {
      setError("Email Change Error");
      setEditEmail(false);
    }
  };

  const handleUploadProfile = (event) => {
    let selected = event.target.files[0];
    if (selected && selected.size < 5 * 10 ** 6) {
      setError("");
      setImage(selected);
    } else {
      setError("Exceeded 5MB");
      setImage(null);
    }
  };

  const handleDeleteProfile = async () => {
    setLoading(true);
    try {
      const imageRef = doc(firestore, "User", currentUser.uid);
      let path = "";
      if (!pathname) {
        const docSnap = await getDoc(imageRef);
        if (docSnap.exists()) {
          path = docSnap.data().photoStoragePath;
        }
      }
      const docStorageRef = ref(storage, pathname ? pathname : path);
      await deleteObject(docStorageRef);
      await updateDoc(imageRef, {
        photoStoragePath: deleteField(),
        photoURL: deleteField(),
      });
      await updateProfilePicture("");
      setLoading(false);
    } catch (err) {
      setError("Deleting Picture Error");
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <Backdrop
          className={classes.backdrop}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <div className={classes.profilePicDiv}>
        <Avatar
          className={classes.profilePic}
          src={url || currentUser.photoURL || Logo}
        />
        <div className={classes.profileButton}>
          <input
            accept="image/*"
            id="change-profile"
            onChange={handleUploadProfile}
            type="file"
            hidden
          />
          <label htmlFor="change-profile">
            <Button variant="outlined" color="primary" component="span">
              Change
            </Button>
          </label>
          <Button
            variant="outlined"
            color="secondary"
            style={{ marginLeft: 16 }}
            onClick={handleDeleteProfile}
            disabled={!currentUser.photoURL}
          >
            Remove
          </Button>
        </div>
        {image && (
          <UploadImage
            image={image}
            setImage={setImage}
            setUrl={setUrl}
            setPathname={setPathname}
          />
        )}
      </div>
      {error && <Alert severity="error">{error}</Alert>}
      <Typography variant="body1" style={{ opacity: 0.6 }}>
        Your Name
      </Typography>
      <Divider />
      <div className={classes.nameDiv}>
        {!editName && (
          <>
            <Typography variant="h5" className={classes.name}>
              {currentUser.displayName}
            </Typography>
            <IconButton onClick={() => setEditName(true)}>
              <Create />
            </IconButton>
          </>
        )}
        {editName && (
          <form onSubmit={handleNameChange}>
            <Input
              className={classes.nameEdit}
              autoFocus
              defaultValue={currentUser.displayName}
              required
              type="text"
              inputRef={nameRef}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton onClick={() => setEditName(false)}>
                    <Close />
                  </IconButton>
                  <IconButton type="submit">
                    <Check />
                  </IconButton>
                </InputAdornment>
              }
            />
          </form>
        )}
      </div>
      <Typography variant="body1" style={{ opacity: 0.6 }}>
        Your Email
      </Typography>
      <Divider />
      <div className={classes.emailDiv}>
        {!editEmail && (
          <>
            <Typography variant="h5" className={classes.email}>
              {currentUser.email}
            </Typography>
            <IconButton onClick={() => setEditEmail(true)}>
              <Create />
            </IconButton>
          </>
        )}
        {editEmail && (
          <form onSubmit={handleEmailChange}>
            <Input
              className={classes.nameEdit}
              autoFocus
              defaultValue={currentUser.email}
              required
              type="text"
              inputRef={emailRef}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton onClick={() => setEditEmail(false)}>
                    <Close />
                  </IconButton>
                  <IconButton type="submit">
                    <Check />
                  </IconButton>
                </InputAdornment>
              }
            />
          </form>
        )}
      </div>
    </>
  );
}
